import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import { AxiosError, AxiosResponse } from 'axios';
import axiosPoll from 'utils/axiosPoll';
import { SELECTED_SPRAY_EVENT_IDS_TABLE_PAGINATION_NAME } from 'scenes/Spray/scenes/Submissions/components/SubmissionsSelectedSprayEventIdsField/constants';
import { SPRAY_EVENT_IDS_TABLE_PAGINATION_NAME } from 'scenes/Spray/scenes/Submissions/components/SubmissionsSprayEventIdsField/constants';
import { CREATE_SPRAY_SUBMISSION_MODAL } from 'scenes/Spray/scenes/Submissions/constants';
import { updateQueryString } from 'services/router/actions';
import { selectSelectedSprayEventIdsForSubmission } from 'services/selectedSprayEventIdsForSubmission/selectors';
import {
    deleteRequest,
    postRequest,
    takeLatestWithCancel
} from 'utils/redux-saga';
// @ts-ignore
import { enqueueSnackbar } from 'services/notifications/actions';
import { resetSelectedSprayEventIdsforSubmission } from 'services/selectedSprayEventIdsForSubmission/actions';
import keyBy from 'lodash.keyby';
import { ERROR_MODAL_QUERY_STRING_PARAM } from 'components/ErrorModal/constants';
import {
    failedCreatedSpraySubmission,
    failedDeleteSpraySubmissions,
    failedLoadSpraySubmissions,
    setSpraySubmissions,
    succeededCreatedSpraySubmission,
    succeededDeleteSpraySubmissions,
    succeededLoadSpraySubmissions
} from './actions';
import {
    SPRAY_SUBMISSIONS_CREATE_REQUESTED,
    SPRAY_SUBMISSIONS_DELETE_NAMESPACE,
    SPRAY_SUBMISSIONS_DELETE_REQUESTED,
    SPRAY_SUBMISSIONS_LOAD_CANCELED,
    SPRAY_SUBMISSIONS_LOAD_REQUESTED
} from './constants';
import {
    RequestDeleteSpraySubmissionsAction,
    RequestedCreateSubmissionAction
} from './types';
import { selectLoadSpraySubmissionsParams } from './params';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* loadSpraySubmissionsSucceeded(
    response: AxiosResponse
): SagaIterator {
    yield all([
        put(succeededLoadSpraySubmissions(response)),
        put(setSpraySubmissions(keyBy(response.data, 'id')))
    ]);
}

export function* loadSpraySubmissionsFailed(error: AxiosError): SagaIterator {
    yield put(failedLoadSpraySubmissions(error));
}

export function* loadSpraySubmissions(): SagaIterator {
    const params = yield select(selectLoadSpraySubmissionsParams);

    yield call(axiosPoll, {
        config: {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/SprayExports`,
            params
        },
        succeeded: loadSpraySubmissionsSucceeded,
        failed: loadSpraySubmissionsFailed
    });
}

export function* createSpraySubmission(
    action: RequestedCreateSubmissionAction
): SagaIterator {
    try {
        const sprayEventIds = yield select(
            selectSelectedSprayEventIdsForSubmission
        );

        const response = yield call(postRequest, 'SprayExports', {
            eventIds: sprayEventIds
        });

        if (response) {
            yield all([
                put(succeededCreatedSpraySubmission(response.data)),
                put(
                    updateQueryString({
                        [CREATE_SPRAY_SUBMISSION_MODAL]: undefined,
                        [SPRAY_EVENT_IDS_TABLE_PAGINATION_NAME]: undefined,
                        [SELECTED_SPRAY_EVENT_IDS_TABLE_PAGINATION_NAME]:
                            undefined
                    })
                ),
                put(resetSelectedSprayEventIdsforSubmission())
            ]);

            yield put(
                enqueueSnackbar({
                    message: 'Submission was created successfully',
                    options: { variant: 'success' }
                })
            );
        }
    } catch (error) {
        yield put(failedCreatedSpraySubmission(error));
    }
}

export function* deleteSpraySubmissions(
    action: RequestDeleteSpraySubmissionsAction
): SagaIterator {
    const id = action.payload;

    try {
        const response = yield call(
            deleteRequest,
            `SprayExportsBySubmission`,
            id
        );

        if (response) {
            yield all([put(succeededDeleteSpraySubmissions(id))]);
        }
    } catch (error) {
        yield all([
            put(failedDeleteSpraySubmissions(error)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]:
                        SPRAY_SUBMISSIONS_DELETE_NAMESPACE
                })
            )
        ]);
    }
}

export function* watchSpraySubmissionCreateRequested(): SagaIterator {
    yield takeLatest(SPRAY_SUBMISSIONS_CREATE_REQUESTED, createSpraySubmission);
}

export function* watchSpraySubmissionsLoadRequested(): SagaIterator {
    yield takeLatestWithCancel(
        SPRAY_SUBMISSIONS_LOAD_REQUESTED,
        SPRAY_SUBMISSIONS_LOAD_CANCELED,
        loadSpraySubmissions
    );
}

export function* watchSpraySubmissionsDeleteRequested(): SagaIterator {
    yield takeLatest(
        SPRAY_SUBMISSIONS_DELETE_REQUESTED,
        deleteSpraySubmissions
    );
}

export default function* spraySubmissionsSagas(): SagaIterator {
    yield all([
        fork(watchSpraySubmissionCreateRequested),
        fork(watchSpraySubmissionsLoadRequested),
        fork(watchSpraySubmissionsDeleteRequested)
    ]);
}
